import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { MdAutoAwesome, MdClose, MdCompareArrows, MdKeyboardArrowDown, MdOutlineChatBubbleOutline } from 'react-icons/md';
import axios from 'config/axios';
import Header from 'components/Header';
import Loader from 'components/Loader';
import {
    type EssayAnalysis,
    type EssaySuggestion,
    type TutorEssayComment,
    type TutorEssayDraft,
    type TutorEssayVersionDetail,
    type PriorReviewedDraft,
    CATEGORY_ORDER,
    CATEGORY_LABELS,
    SUGGESTION_CATEGORY_LABELS,
    SEVERITY_LABELS,
    promptNumber,
} from 'global/essays';
import { type DiffRange, addedRanges } from 'global/diff';

import './index.css';

const POLL_MS = 2000;
const RING_CIRC = 263.9;   // 2πr, r=42 (matches the score-ring SVG)
type RunStatus = 'idle' | 'running' | 'succeeded' | 'failed';

interface EssayMeta {
    id: number;
    title: string;
    prompt: string;
    essayType: 'personal' | 'supplement';
    collegeName: string | null;
    wordLimit: number | null;
    studentOrganizationId: number;
    studentName: string;
}

// A pending composer: a manual selection OR a click-to-publish from a suggestion.
// (General whole-essay comments use the always-visible box in the comments card.)
interface Composer {
    start: number;
    end: number;
    anchorText: string;
    body: string;
    fromAI: boolean;
    suggestionId: string | null;
    top: number;
    left: number;
}

interface Segment { text: string; kind?: 'comment' | 'ai' | 'live' | 'added'; comment?: TutorEssayComment; suggestion?: EssaySuggestion; }

// Walk text nodes to convert a (node, offset) DOM position to a global char index.
function globalOffset(root: Node, node: Node, offset: number): number {
    let total = 0;
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
    let n: Node | null;
    while ((n = walker.nextNode())) {
        if (n === node) return total + offset;
        total += (n.nodeValue || '').length;
    }
    return total;
}

// Build a DOM Range spanning global char offsets [start,end] across the rendered text.
function domRangeForOffsets(root: Node, start: number, end: number): Range | null {
    const range = document.createRange();
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
    let pos = 0, setS = false, setE = false;
    let n: Node | null;
    while ((n = walker.nextNode())) {
        const len = (n.nodeValue || '').length;
        if (!setS && start <= pos + len) { range.setStart(n, start - pos); setS = true; }
        if (!setE && end <= pos + len) { range.setEnd(n, end - pos); setE = true; break; }
        pos += len;
    }
    return (setS && setE) ? range : null;
}

// Draft status pill, mirroring the student site: EDITING (unsubmitted),
// IN REVIEW (submitted, review not finished), comment-count chip (finished).
function DraftStatePill({ d }: { d: { submittedAt: string | null; commentCount: number; unsentCount: number } }) {
    const finished = d.commentCount > 0 && d.unsentCount === 0;
    if (finished) {
        return <span className='essayRev__cmtChip'><MdOutlineChatBubbleOutline size={11} /> {d.commentCount}</span>;
    }
    if (d.submittedAt) return <span className='essayRev__statePill essayRev__statePill--review'>IN REVIEW</span>;
    return <span className='essayRev__statePill essayRev__statePill--edit'>EDITING</span>;
}

// Scroll the WINDOW so el sits mid-viewport. el.scrollIntoView would also
// scroll overflow:hidden ancestors (the essay page / comments cards), visually
// shoving their content upward with no scrollbar to recover — the window is
// the only scroller we actually want to move.
function scrollWindowTo(el: Element | null) {
    if (!el) return;
    // Find the ancestor that actually scrolls (overflow-y auto/scroll with
    // overflowing content) — the app shell may scroll a container instead of
    // the window. overflow:hidden ancestors are deliberately skipped.
    let scroller: Element | null = null;
    for (let p = el.parentElement; p; p = p.parentElement) {
        const oy = getComputedStyle(p).overflowY;
        if ((oy === 'auto' || oy === 'scroll') && p.scrollHeight > p.clientHeight + 1) { scroller = p; break; }
    }
    const r = el.getBoundingClientRect();
    if (scroller) {
        const c = scroller.getBoundingClientRect();
        scroller.scrollTo({
            top: scroller.scrollTop + (r.top - c.top) + r.height / 2 - scroller.clientHeight / 2,
            behavior: 'smooth',
        });
    } else {
        window.scrollTo({ top: window.scrollY + r.top + r.height / 2 - window.innerHeight / 2, behavior: 'smooth' });
    }
}

// Merge comment ranges (brand), pending AI suggestions (indigo) and an optional
// live range into ordered segments. First-wins on overlap; live takes priority.
function buildSegments(
    content: string,
    comments: TutorEssayComment[],
    suggestions: EssaySuggestion[],
    live: { start: number; end: number } | null,
    added: DiffRange[] = [],
): Segment[] {
    type R = { start: number; end: number; kind: 'live' | 'comment' | 'ai'; comment?: TutorEssayComment; suggestion?: EssaySuggestion };
    const ranges: R[] = [];
    if (live && live.end > live.start) ranges.push({ start: live.start, end: live.end, kind: 'live' });
    for (const c of comments) ranges.push({ start: c.anchorStart, end: c.anchorEnd, kind: 'comment', comment: c });
    for (const s of suggestions) ranges.push({ start: s.anchorStart, end: s.anchorEnd, kind: 'ai', suggestion: s });
    const prio: Record<string, number> = { live: 0, comment: 1, ai: 2 };
    ranges.sort((a, b) => a.start - b.start || prio[a.kind] - prio[b.kind]);

    const segs: Segment[] = [];
    let pos = 0;
    for (const r of ranges) {
        if (r.start < pos || r.end <= r.start || r.end > content.length) continue;
        if (r.start > pos) segs.push({ text: content.slice(pos, r.start) });
        segs.push({ text: content.slice(r.start, r.end), kind: r.kind, comment: r.comment, suggestion: r.suggestion });
        pos = r.end;
    }
    if (pos < content.length) segs.push({ text: content.slice(pos) });
    if (!added.length) return segs;

    // Second pass (compare mode): split only the PLAIN segments against the diff's
    // added ranges, so comment/AI/live highlights always win visually and green fills
    // only otherwise-unhighlighted text.
    const out: Segment[] = [];
    let off = 0;
    for (const seg of segs) {
        const segStart = off, segEnd = off + seg.text.length;
        off = segEnd;
        if (seg.kind) { out.push(seg); continue; }
        let cur = segStart;
        for (const r of added) {
            const s = Math.max(r.start, cur), e = Math.min(r.end, segEnd);
            if (e <= s) continue;
            if (s > cur) out.push({ text: content.slice(cur, s) });
            out.push({ text: content.slice(s, e), kind: 'added' });
            cur = e;
        }
        if (cur < segEnd) out.push({ text: content.slice(cur, segEnd) });
    }
    return out;
}

function FillAppEssayReview() {
    const navigate = useNavigate();
    const { versionId } = useParams<{ versionId: string }>();

    const [essay, setEssay] = useState<EssayMeta | null>(null);
    const [version, setVersion] = useState<TutorEssayVersionDetail | null>(null);
    const [drafts, setDrafts] = useState<TutorEssayDraft[]>([]);
    const [comments, setComments] = useState<TutorEssayComment[]>([]);
    const [analysis, setAnalysis] = useState<EssayAnalysis | null>(null);
    const [loadError, setLoadError] = useState<string | null>(null);

    const [runStatus, setRunStatus] = useState<RunStatus>('idle');
    const [runError, setRunError] = useState('');
    const [pollId, setPollId] = useState<number | null>(null);

    const [composer, setComposer] = useState<Composer | null>(null);
    // Hover linking: comment card <-> its highlighted passage.
    const [hoverCommentId, setHoverCommentId] = useState<number | null>(null);
    // Hover linking for AI suggestions, mirroring hoverCommentId: hovering a suggestion
    // card lights its passage up, hovering the passage lights the card up. General
    // (unanchored) suggestions have no passage and stay out of this.
    const [hoverSuggId, setHoverSuggId] = useState<string | null>(null);
    // In-place editor for an EXISTING comment, opened by clicking its highlight.
    const [editPop, setEditPop] = useState<{ id: number; body: string; top: number; left: number } | null>(null);
    const [editPopError, setEditPopError] = useState('');
    const [editPopSaving, setEditPopSaving] = useState(false);
    const [editingId, setEditingId] = useState<number | null>(null);
    const [editBody, setEditBody] = useState('');
    const [saving, setSaving] = useState(false);
    const [sending, setSending] = useState(false);
    const [composerError, setComposerError] = useState('');   // shown inside the composer popup
    const [commentsError, setCommentsError] = useState('');   // shown in the comments card

    // Always-visible general (whole-essay) comment box in the comments card.
    // Independent of the anchored popup composer so the two never interfere.
    const [generalBody, setGeneralBody] = useState('');
    // Set when the box was prefilled from a GENERAL AI suggestion: its id, so saving
    // flips that suggestion to published exactly like the anchored composer does.
    const [generalFromSuggestionId, setGeneralFromSuggestionId] = useState<string | null>(null);
    const [generalSaving, setGeneralSaving] = useState(false);
    const [generalError, setGeneralError] = useState('');

    // Side-by-side compare vs the nearest earlier reviewed draft.
    const [priorReviewed, setPriorReviewed] = useState<PriorReviewedDraft | null>(null);
    const [compareOn, setCompareOn] = useState(false);

    const manuscriptRef = useRef<HTMLDivElement | null>(null);

    // Draft picker dropdown (replaces the old left drafts rail).
    const [draftsOpen, setDraftsOpen] = useState(false);
    const draftPickRef = useRef<HTMLDivElement | null>(null);
    useEffect(() => {
        if (!draftsOpen) return;
        const onDoc = (e: MouseEvent) => {
            if (draftPickRef.current && !draftPickRef.current.contains(e.target as Node)) setDraftsOpen(false);
        };
        document.addEventListener('mousedown', onDoc);
        return () => document.removeEventListener('mousedown', onDoc);
    }, [draftsOpen]);

    // Load version + essay meta + drafts + comments + latest analysis.
    useEffect(() => {
        if (!versionId) return;
        let cancelled = false;
        setComposer(null);
        setEditPop(null);
        setCompareOn(false);
        setGeneralBody(''); setGeneralFromSuggestionId(null); setGeneralError('');
        axios.get(`fillapp/tutor/version/${versionId}/`)
            .then(res => {
                if (cancelled) return;
                setEssay(res.data.essay);
                setVersion(res.data.version);
                setDrafts(res.data.drafts || []);
                setComments(res.data.comments || []);
                setPriorReviewed(res.data.priorReviewed || null);
                const latest: EssayAnalysis | null = res.data.latestAnalysis;
                setAnalysis(latest);
                if (latest && (latest.status === 'running' || latest.status === 'queued')) {
                    setRunStatus('running'); setPollId(latest.id);
                } else if (latest && latest.status === 'succeeded') {
                    setRunStatus('succeeded');
                } else if (latest && latest.status === 'failed') {
                    setRunStatus('failed'); setRunError(latest.errorMessage || '');
                } else {
                    setRunStatus('idle');
                }
            })
            .catch(err => { if (!cancelled) setLoadError(err?.response?.data?.message || 'Failed to load draft.'); });
        return () => { cancelled = true; };
    }, [versionId]);

    // Poll an in-flight analysis until it terminates.
    useEffect(() => {
        if (pollId == null) return;
        let stop = false;
        let fails = 0;
        const tick = () => {
            axios.get(`fillapp/tutor/analysis/${pollId}/status/`)
                .then(res => {
                    if (stop) return;
                    fails = 0;
                    const st = res.data.status;
                    if (st === 'succeeded') { setAnalysis(res.data.analysis); setRunStatus('succeeded'); setPollId(null); }
                    else if (st === 'failed') { setRunStatus('failed'); setRunError(res.data.errorMessage || 'Analysis failed.'); setPollId(null); }
                })
                .catch(() => { if (stop) return; fails += 1; if (fails >= 3) { setRunStatus('failed'); setRunError('Lost connection while analyzing.'); setPollId(null); } });
        };
        const iv = setInterval(tick, POLL_MS);
        tick();
        return () => { stop = true; clearInterval(iv); };
    }, [pollId]);

    const runAnalysis = () => {
        if (runStatus === 'running') return;
        setRunStatus('running'); setRunError('');
        axios.post(`fillapp/tutor/version/${versionId}/analyze/`)
            .then(res => setPollId(res.data.analysisId))
            .catch(err => { setRunStatus('failed'); setRunError(err?.response?.data?.message || 'Could not start analysis.'); });
    };

    const pendingSuggestions = useMemo(
        () => (analysis?.status === 'succeeded' ? (analysis.suggestions || []).filter(s => s.status === 'pending') : []),
        [analysis],
    );

    // Word-diff vs the prior reviewed draft — computed only in compare mode.
    // GitHub-style: additions highlight green in the CURRENT pane, deletions red
    // in the PRIOR pane (removed = ranges of prior not present in current).
    const diffAdded = useMemo<DiffRange[]>(
        () => (compareOn && priorReviewed && version ? addedRanges(priorReviewed.content, version.content) : []),
        [compareOn, priorReviewed, version],
    );
    const diffRemoved = useMemo<DiffRange[]>(
        () => (compareOn && priorReviewed && version ? addedRanges(version.content, priorReviewed.content) : []),
        [compareOn, priorReviewed, version],
    );

    const positionFor = (start: number, end: number): { top: number; left: number } => {
        const root = manuscriptRef.current;
        if (!root) return { top: 0, left: 0 };
        const range = domRangeForOffsets(root, start, end);
        if (!range) return { top: 0, left: 0 };
        const r = range.getBoundingClientRect();
        const c = root.getBoundingClientRect();
        let left = r.left - c.left;
        const maxLeft = root.clientWidth - 300;
        if (left > maxLeft) left = Math.max(8, maxLeft);
        // Position viewport-aware so the whole popup (incl. its button) stays visible.
        // Default below the selection; if that would run past the bottom of the viewport
        // (e.g. a tall multi-line selection, or the essay's end), flip above the
        // selection instead. r is in viewport coords; convert to manuscript-relative.
        const POPUP_H = 220;   // approx composer height, used only to decide the flip
        const GAP = 10;
        const MARGIN = 12;     // keep off the very top/bottom edges of the viewport
        let topVp = r.bottom + GAP;                      // below the selection
        if (topVp + POPUP_H > window.innerHeight - MARGIN) {
            topVp = r.top - POPUP_H - GAP;               // flip: above the selection
        }
        if (topVp < MARGIN) topVp = MARGIN;              // never above the viewport top
        const top = Math.max(8, topVp - c.top);          // viewport -> manuscript-relative
        return { top, left: Math.max(8, left) };
    };

    // --- manual comment: select text in the manuscript ---
    const onManuscriptMouseUp = () => {
        if (!version) return;
        // Commentable only on the latest draft AND after the student submitted it.
        if (!version.submittedAt || drafts.some(d => d.versionNumber > version.versionNumber)) return;
        const sel = window.getSelection();
        const root = manuscriptRef.current;
        if (!sel || sel.rangeCount === 0 || sel.isCollapsed || !root) return;
        const range = sel.getRangeAt(0);
        if (!root.contains(range.startContainer) || !root.contains(range.endContainer)) return;
        let start = globalOffset(root, range.startContainer, range.startOffset);
        let end = globalOffset(root, range.endContainer, range.endOffset);
        if (end < start) [start, end] = [end, start];
        const text = version.content.slice(start, end);
        if (!text.trim()) return;
        setComposerError('');
        setComposer({ start, end, anchorText: text, body: '', fromAI: false, suggestionId: null, ...positionFor(start, end) });
    };

    // --- GENERAL AI suggestion (no anchor) → prefill the general comment box.
    // The anchored composer needs a span; a whole-essay note goes through the same
    // draft→send lifecycle as a hand-written general comment.
    const openFromGeneralSuggestion = (s: EssaySuggestion) => {
        if (!version || !version.submittedAt || drafts.some(d => d.versionNumber > version.versionNumber)) return;
        setGeneralBody(s.body);
        setGeneralFromSuggestionId(s.id);
        setGeneralError('');
        scrollWindowTo(document.querySelector('.essayRev__gcomposer'));
    };

    // --- click an AI suggestion: jump to its line + open the composer pre-filled ---
    // Popup placement in manuscript coordinates, always below the anchor — used
    // when we're about to recenter the window on the anchor anyway, where
    // positionFor's viewport-based flip would decide from a stale scroll position.
    const positionBelow = (start: number, end: number): { top: number; left: number } => {
        const root = manuscriptRef.current;
        if (!root) return { top: 0, left: 0 };
        const range = domRangeForOffsets(root, start, end);
        if (!range) return { top: 0, left: 0 };
        const r = range.getBoundingClientRect();
        const c = root.getBoundingClientRect();
        let left = r.left - c.left;
        const maxLeft = root.clientWidth - 300;
        if (left > maxLeft) left = Math.max(8, maxLeft);
        return { top: r.bottom - c.top + 10, left: Math.max(8, left) };
    };

    const openFromSuggestion = (s: EssaySuggestion) => {
        if (!version || !version.submittedAt || drafts.some(d => d.versionNumber > version.versionNumber)) return;
        const root = manuscriptRef.current;
        const range = root ? domRangeForOffsets(root, s.anchorStart, s.anchorEnd) : null;
        if (range) scrollWindowTo(range.startContainer.parentElement);
        setComposerError('');
        setComposer({
            start: s.anchorStart, end: s.anchorEnd, anchorText: s.anchorText,
            body: s.body, fromAI: true, suggestionId: s.id, ...positionBelow(s.anchorStart, s.anchorEnd),
        });
    };

    // Click a commented highlight: edit that comment in place, right where it
    // was written, instead of jumping down to the notes list.
    const openEditPopup = (c: TutorEssayComment) => {
        setComposer(null);
        setEditPopError('');
        setEditPop({ id: c.id, body: c.body, ...positionFor(c.anchorStart, c.anchorEnd) });
    };

    const saveEditPopup = () => {
        if (!editPop || !editPop.body.trim() || editPopSaving) return;
        setEditPopSaving(true);
        setEditPopError('');
        axios.post(`fillapp/tutor/comment/${editPop.id}/`, { body: editPop.body.trim() })
            .then(res => {
                setComments(list => list.map(x => (x.id === editPop.id ? res.data.comment : x)));
                setEditPop(null);
            })
            .catch(err => {
                setEditPopError(err?.response?.data?.message || 'Could not save the comment.');
                if (err?.response?.status === 409) refreshData();
            })
            .finally(() => setEditPopSaving(false));
    };

    const deleteFromPopup = () => {
        if (!editPop) return;
        const target = comments.find(x => x.id === editPop.id);
        setEditPop(null);
        if (target) deleteComment(target);
    };

    // Re-sync page data after a server-side rejection — e.g. the student branched a
    // newer draft while this page was open, so the local drafts/notices are stale.
    // Leaves the composer alone; the gates and banners recompute from fresh data.
    const refreshData = () => {
        axios.get(`fillapp/tutor/version/${versionId}/`)
            .then(res => {
                setEssay(res.data.essay);
                setVersion(res.data.version);
                setDrafts(res.data.drafts || []);
                setComments(res.data.comments || []);
                setPriorReviewed(res.data.priorReviewed || null);
            })
            .catch(() => { /* keep current view */ });
    };

    const addComment = () => {
        if (!composer || !composer.body.trim() || saving) return;
        setSaving(true);
        setComposerError('');
        axios.post(`fillapp/tutor/version/${versionId}/comment/`, {
            anchorStart: composer.start,
            anchorEnd: composer.end,
            anchorText: composer.anchorText,
            body: composer.body.trim(),
            sourceSuggestionId: composer.suggestionId || undefined,
        })
            .then(res => {
                const created: TutorEssayComment = res.data.comment;
                setComments(c => [...c, created]);
                // Flip the source suggestion to published so it leaves the list.
                if (composer.suggestionId) {
                    setAnalysis(a => a ? {
                        ...a,
                        suggestions: a.suggestions.map(s => s.id === composer.suggestionId
                            ? { ...s, status: 'published', publishedCommentId: created.id } : s),
                    } : a);
                }
                setComposer(null);
            })
            .catch(err => {
                // Keep the composer open and SAY why it failed (409 = not submitted /
                // newer draft exists). Refresh so stale gates/banners correct themselves.
                setComposerError(err?.response?.data?.message || 'Could not add the comment. Please try again.');
                if (err?.response?.status === 409) refreshData();
            })
            .finally(() => setSaving(false));
    };

    // General (whole-essay) comment from the always-visible box — sentinel anchor,
    // same draft→send lifecycle as anchored comments.
    const addGeneralComment = () => {
        const body = generalBody.trim();
        if (!body || generalSaving) return;
        setGeneralSaving(true);
        setGeneralError('');
        const sourceId = generalFromSuggestionId;
        axios.post(`fillapp/tutor/version/${versionId}/comment/`, {
            general: true,
            body,
            sourceSuggestionId: sourceId || undefined,
        })
            .then(res => {
                const created: TutorEssayComment = res.data.comment;
                setComments(c => [...c, created]);
                // Flip the source suggestion to published so it leaves the list —
                // same contract as the anchored composer.
                if (sourceId) {
                    setAnalysis(a => a ? {
                        ...a,
                        suggestions: a.suggestions.map(s => s.id === sourceId
                            ? { ...s, status: 'published', publishedCommentId: created.id } : s),
                    } : a);
                }
                setGeneralBody('');
                setGeneralFromSuggestionId(null);
            })
            .catch(err => {
                setGeneralError(err?.response?.data?.message || 'Could not add the comment. Please try again.');
                if (err?.response?.status === 409) refreshData();
            })
            .finally(() => setGeneralSaving(false));
    };

    const startEdit = (c: TutorEssayComment) => { setEditingId(c.id); setEditBody(c.body); setCommentsError(''); };
    const cancelEdit = () => { setEditingId(null); setEditBody(''); };
    const saveEdit = () => {
        if (editingId == null || !editBody.trim()) return;
        axios.post(`fillapp/tutor/comment/${editingId}/`, { body: editBody.trim() })
            .then(res => {
                setComments(list => list.map(x => x.id === editingId ? res.data.comment : x));
                setEditingId(null);
                setEditBody('');
                setCommentsError('');
            })
            .catch(err => {
                setCommentsError(err?.response?.data?.message || 'Could not save the comment.');
                if (err?.response?.status === 409) refreshData();
            });
    };

    const deleteComment = (c: TutorEssayComment) => {
        axios.post(`fillapp/tutor/comment/${c.id}/delete/`)
            .then(() => {
                setComments(list => list.filter(x => x.id !== c.id));
                setCommentsError('');
                // If it came from a suggestion, return that suggestion to the pending list.
                if (c.sourceSuggestionId) {
                    setAnalysis(a => a ? {
                        ...a,
                        suggestions: a.suggestions.map(s => s.id === c.sourceSuggestionId
                            ? { ...s, status: 'pending', publishedCommentId: null } : s),
                    } : a);
                }
            })
            .catch(err => {
                setCommentsError(err?.response?.data?.message || 'Could not delete the comment.');
                if (err?.response?.status === 409) refreshData();
            });
    };

    // Publish all private-draft comments at once — the "review done" signal to the student.
    const unsentCount = comments.filter(c => !c.isPublished).length;
    const sendComments = () => {
        if (unsentCount === 0 || sending) return;
        setSending(true);
        axios.post(`fillapp/tutor/version/${versionId}/comments/send/`)
            .then(res => { setComments(res.data.comments || []); setCommentsError(''); })
            .catch(err => {
                setCommentsError(err?.response?.data?.message || 'Could not send the comments.');
                if (err?.response?.status === 409) refreshData();
            })
            .finally(() => setSending(false));
    };

    if (loadError) return <div className='essayRev'><div className='essayRev__error'>{loadError}</div></div>;
    if (!essay || !version) return <div className='essayRev'><Loader /></div>;

    // Supplements are manual-review only: the v1 rubric is written for the
    // personal statement, so the AI cards/legend are hidden and the backend
    // rejects analyze calls.
    const isSupp = essay.essayType === 'supplement';
    const wordLimit = isSupp ? essay.wordLimit : null;
    const overLimit = !!wordLimit && version.wordCount > wordLimit;
    const num = isSupp ? null : promptNumber(essay.prompt);
    // Only the latest draft is commentable; older drafts are view-only once the student has moved on.
    const isLatest = !drafts.some(d => d.versionNumber > version.versionNumber);
    // A draft is reviewable only after the student submits it — commenting locks the
    // version, which would freeze their in-progress draft. Mirrors the backend guards.
    const isSubmitted = !!version.submittedAt;
    const hasContent = version.content.trim() !== '';
    const canReview = isLatest && isSubmitted;
    const awaitingReview = canReview && comments.length === 0;
    // The drafts payload is a load-time snapshot; comments mutate live. For the
    // version on screen, derive its status counts from the live comments state so
    // the dropdown pill flips the moment a review is finished or reopened.
    const liveDraft = (d: TutorEssayDraft): TutorEssayDraft => (
        version && d.id === version.id
            ? {
                ...d,
                commentCount: comments.filter(c => c.isPublished).length,
                unsentCount: comments.filter(c => !c.isPublished).length,
            }
            : d
    );

    // Tutor list: newest comment first (the student-side lists stay in essay order).
    const sortedComments = [...comments].sort(
        (a, b) => Date.parse(b.createdAt) - Date.parse(a.createdAt),
    );
    const segments = buildSegments(
        version.content,
        comments,
        pendingSuggestions,
        composer ? { start: composer.start, end: composer.end } : null,
        diffAdded,
    );

    return (
        <div className='essayRev'>
            <Helmet><title>{essay.title} · {version.label}</title></Helmet>
            <Header
                title={essay.title}
                prevRoutes={[
                    { name: 'College Apps', path: '/college-apps' },
                    { name: essay.studentName, path: `/college-apps/review/${essay.studentOrganizationId}` },
                    { name: 'Essays', path: `/college-apps/essays/${essay.studentOrganizationId}` },
                ]}
            />

            <div className='essayRev__bar'>
                <div>
                    <div className='essayRev__eyebrow'>
                        {isSupp
                            ? `College supplement${essay.collegeName ? ` · ${essay.collegeName}` : ''}`
                            : `Common Application${num ? ` · Prompt ${num}` : ''}`} · {essay.studentName}
                    </div>
                    <h1 className='essayRev__idTitle'>{essay.title}</h1>
                </div>
                <div className='essayRev__legend'>
                    <span className='essayRev__key'><span className='essayRev__sw essayRev__sw--c' />Comments</span>
                    {!isSupp && <span className='essayRev__key'><span className='essayRev__sw essayRev__sw--ai' />AI</span>}
                </div>
            </div>

            <div className={`essayRev__shell ${compareOn && priorReviewed ? 'essayRev__shell--compare' : ''}`}>
                {/* center: prompt + manuscript + comments */}
                <div className='essayRev__center'>
                    {essay.prompt && (
                        <div className='essayRev__ribbon'>
                            {isSupp
                                ? <span className='essayRev__ribbonTag'>SUPPLEMENT</span>
                                : num && <span className='essayRev__ribbonTag'>PROMPT {num}</span>}
                            <p>{essay.prompt}</p>
                        </div>
                    )}

                    {!isLatest && (
                        <div className='essayRev__notice'>
                            The student has a newer draft. Commenting is disabled here — open the latest draft to leave feedback.
                        </div>
                    )}

                    {isLatest && !isSubmitted && (
                        <div className='essayRev__notice'>
                            {essay.studentName} hasn't submitted this draft yet. You can review it
                            {isSupp ? '' : ' — comment and run AI analysis —'} once they submit it for review.
                        </div>
                    )}

                    <div className={compareOn && priorReviewed ? 'essayRev__compareGrid' : undefined}>
                    {compareOn && priorReviewed && (
                        <div className='essayRev__page essayRev__page--prior'>
                            <div className='essayRev__pageBar'>
                                <span className='essayRev__pageBarInfo'>
                                    {priorReviewed.label} · {priorReviewed.comments.length} comment{priorReviewed.comments.length === 1 ? '' : 's'}
                                </span>
                                <span className='essayRev__hint'>What the student saw</span>
                            </div>
                            <div className='essayRev__manuscript'>
                                {buildSegments(priorReviewed.content, priorReviewed.comments as unknown as TutorEssayComment[], [], null, diffRemoved).map((seg, i) => (
                                    seg.kind === 'comment'
                                        ? <span key={i} className='essayRev__cHl' title={seg.comment!.body}>{seg.text}</span>
                                        : seg.kind === 'added'
                                            ? <span key={i} className='essayRev__delHl' title='Removed or changed in the newer draft'>{seg.text}</span>
                                            : <span key={i}>{seg.text}</span>
                                ))}
                            </div>
                        </div>
                    )}
                    <div className='essayRev__page'>
                        <div className='essayRev__pageBar'>
                            {compareOn ? (
                                // Compact bar at half width — details live in the full-width view.
                                <>
                                    <span className='essayRev__pageBarInfo'>
                                        {version.label} · <span className={overLimit ? 'essayRev__wordsOver' : ''}>
                                            {version.wordCount}{wordLimit ? ` / ${wordLimit}` : ''} words
                                        </span>
                                    </span>
                                    <button
                                        type='button'
                                        className='essayRev__compareBtn'
                                        onClick={() => { setComposer(null); setEditPop(null); setCompareOn(false); }}
                                    >
                                        <MdClose size={14} /> Exit compare
                                    </button>
                                </>
                            ) : (
                                <>
                                    <span className='essayRev__pageBarInfo'>
                                        <span className='essayRev__hint'>
                                            {!isLatest ? 'Read-only — newer draft exists'
                                                : !isSubmitted ? 'Read-only — not submitted yet'
                                                : 'Select text to leave a comment'}
                                        </span>
                                        {awaitingReview && <span className='essayRev__await'>Awaiting your review</span>}
                                        {overLimit && (
                                            <span className='essayRev__wordsOver'>
                                                {version.wordCount}{wordLimit ? ` / ${wordLimit}` : ''} words
                                            </span>
                                        )}
                                    </span>
                                    {priorReviewed && isLatest && isSubmitted && (
                                        <button
                                            type='button'
                                            className='essayRev__compareBtn'
                                            onClick={() => { setComposer(null); setEditPop(null); setCompareOn(true); }}
                                        >
                                            <MdCompareArrows size={15} /> Compare with {priorReviewed.label}
                                        </button>
                                    )}
                                </>
                            )}
                        </div>
                        <div className='essayRev__manuscriptWrap'>
                        <div className='essayRev__manuscript' ref={manuscriptRef} onMouseUp={onManuscriptMouseUp}>
                            {segments.map((seg, i) => {
                                if (seg.kind === 'live') return <span key={i} className='essayRev__sel'>{seg.text}</span>;
                                if (seg.kind === 'comment') {
                                    return (
                                        <span
                                            key={i}
                                            className={`essayRev__cHl ${seg.comment!.isResolved ? 'essayRev__cHl--resolved' : ''} ${hoverCommentId === seg.comment!.id ? 'essayRev__cHl--hover' : ''}`}
                                            title={seg.comment!.body}
                                            onMouseEnter={() => setHoverCommentId(seg.comment!.id)}
                                            onMouseLeave={() => setHoverCommentId(null)}
                                            onClick={() => (canReview
                                                ? openEditPopup(seg.comment!)
                                                : scrollWindowTo(document.getElementById(`tc-${seg.comment!.id}`)))}
                                        >{seg.text}</span>
                                    );
                                }
                                if (seg.kind === 'ai') {
                                    // Severity shows only on the suggestion card's chip;
                                    // all AI highlights share the same indigo in the text.
                                    return (
                                        <span
                                            key={i}
                                            className={`essayRev__aiHl ${hoverSuggId === seg.suggestion!.id ? 'essayRev__aiHl--hover' : ''}`}
                                            title={`AI: ${SUGGESTION_CATEGORY_LABELS[seg.suggestion!.category] || seg.suggestion!.category}`}
                                            onMouseEnter={() => setHoverSuggId(seg.suggestion!.id)}
                                            onMouseLeave={() => setHoverSuggId(null)}
                                            onClick={() => openFromSuggestion(seg.suggestion!)}
                                        >{seg.text}</span>
                                    );
                                }
                                if (seg.kind === 'added') {
                                    return <span key={i} className='essayRev__addHl' title={`New or edited since ${priorReviewed?.label || 'the reviewed draft'}`}>{seg.text}</span>;
                                }
                                return <span key={i}>{seg.text}</span>;
                            })}
                        </div>

                            {editPop && (() => {
                                const target = comments.find(x => x.id === editPop.id);
                                return (
                                    <div className='essayRev__cpop' style={{ top: editPop.top, left: editPop.left }}>
                                        {target?.anchorText && <div className='essayRev__cpopAnchor'>"{target.anchorText}"</div>}
                                        <textarea
                                            className='essayRev__cpopText'
                                            value={editPop.body}
                                            onChange={e => setEditPop(p => p ? { ...p, body: e.target.value } : p)}
                                            rows={3}
                                            autoFocus
                                        />
                                        {editPopError && <div className='essayRev__cpopError'>{editPopError}</div>}
                                        <div className='essayRev__cpopAct'>
                                            <button className='essayRev__btn essayRev__btn--danger' onClick={deleteFromPopup} disabled={editPopSaving}>Delete</button>
                                            <span className='essayRev__cpopSpacer' />
                                            <button className='essayRev__btn essayRev__btn--ghost' onClick={() => setEditPop(null)} disabled={editPopSaving}>Cancel</button>
                                            <button className='essayRev__btn essayRev__btn--primary' onClick={saveEditPopup} disabled={editPopSaving || !editPop.body.trim()}>
                                                {editPopSaving ? 'Saving…' : 'Save'}
                                            </button>
                                        </div>
                                    </div>
                                );
                            })()}
                            {composer && (
                                <div className='essayRev__cpop' style={{ top: composer.top, left: composer.left }}>
                                    {composer.fromAI && (
                                        <div className='essayRev__cpopFrom'>
                                            <MdAutoAwesome size={13} /> From AI suggestion — edit before it reaches the student
                                        </div>
                                    )}
                                    <div className='essayRev__cpopAnchor'>"{composer.anchorText}"</div>
                                    <textarea
                                        className='essayRev__cpopText'
                                        value={composer.body}
                                        onChange={e => setComposer(c => c ? { ...c, body: e.target.value } : c)}
                                        placeholder='Write a comment the student will see…'
                                        rows={3}
                                        autoFocus
                                    />
                                    {composerError && <div className='essayRev__cpopError'>{composerError}</div>}
                                    <div className='essayRev__cpopAct'>
                                        <button className='essayRev__btn essayRev__btn--ghost' onClick={() => { setComposer(null); setComposerError(''); }} disabled={saving}>Cancel</button>
                                        <button className='essayRev__btn essayRev__btn--primary' onClick={addComment} disabled={saving || !composer.body.trim()}>
                                            {saving ? 'Adding…' : 'Add comment'}
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                    </div>{/* /compareGrid (or plain wrapper) */}

                    {/* comments to student */}
                    <div className='essayRev__comments'>
                        <div className='essayRev__commentsHead'>
                            <h3>Comments to student</h3>
                            <span className='essayRev__commentsCount'>{comments.length}</span>
                            <button
                                className='essayRev__sendBtn'
                                onClick={sendComments}
                                disabled={comments.length === 0 || unsentCount === 0 || sending}
                            >
                                {sending ? 'Finishing…'
                                    : comments.length === 0 ? 'Finish review'
                                    : unsentCount > 0 ? `Finish review (${unsentCount})`
                                    : 'Review finished ✓'}
                            </button>
                        </div>
                        <p className='essayRev__commentsSub'>
                            Comments stay private until you click <b>Finish review</b> — that shares them all
                            and lets the student start a new draft. Adding another comment afterward reopens
                            the review until you finish again.
                        </p>
                        {commentsError && <div className='essayRev__commentsError'>{commentsError}</div>}
                        {canReview && (
                            <div className='essayRev__gcomposer'>
                                {generalFromSuggestionId && (
                                    <div className='essayRev__cpopFrom'>
                                        <MdAutoAwesome size={13} /> From AI suggestion — edit before it reaches the student
                                        <button
                                            type='button'
                                            className='essayRev__cpopFromClear'
                                            title='Discard — returns the suggestion to the list'
                                            onClick={() => { setGeneralBody(''); setGeneralFromSuggestionId(null); }}
                                        >
                                            <MdClose size={14} />
                                        </button>
                                    </div>
                                )}
                                <textarea
                                    className='essayRev__cpopText'
                                    value={generalBody}
                                    onChange={e => {
                                        setGeneralBody(e.target.value);
                                        if (e.target.value === '') setGeneralFromSuggestionId(null);
                                    }}
                                    placeholder='Leave a general comment about the whole essay…'
                                    rows={generalFromSuggestionId ? 4 : 2}
                                />
                                {generalError && <div className='essayRev__cpopError'>{generalError}</div>}
                                <div className='essayRev__cpopAct'>
                                    <button
                                        className='essayRev__btn essayRev__btn--primary'
                                        onClick={addGeneralComment}
                                        disabled={generalSaving || !generalBody.trim()}
                                    >
                                        {generalSaving ? 'Adding…' : 'Add general comment'}
                                    </button>
                                </div>
                            </div>
                        )}
                        <div className='essayRev__commentsList'>
                            {comments.length === 0 && (
                                <p className='essayRev__muted'>No comments yet. Select text above, click an AI suggestion, or write a general comment in the box above.</p>
                            )}
                            {sortedComments.map(c => (
                                <div
                                    key={c.id}
                                    id={`tc-${c.id}`}
                                    className={`essayRev__note ${c.isPublished ? '' : 'essayRev__note--draft'} ${hoverCommentId === c.id && !c.isGeneral ? 'essayRev__note--hover' : ''}`}
                                    onMouseEnter={() => !c.isGeneral && setHoverCommentId(c.id)}
                                    onMouseLeave={() => setHoverCommentId(null)}
                                >
                                    <div className='essayRev__noteMeta'>
                                        <span className='essayRev__noteWho'>{initials(c.authorName) || 'You'}</span>
                                        <span className='essayRev__noteAuthor'>You</span>
                                        <span className={`essayRev__noteState ${c.isPublished ? 'essayRev__noteState--sent' : 'essayRev__noteState--draft'}`}>
                                            {c.isPublished ? 'Sent' : 'Draft'}
                                        </span>
                                        {c.isResolved && (
                                            <span className='essayRev__noteState essayRev__noteState--resolved'>Resolved</span>
                                        )}
                                        <span className='essayRev__noteTime'>{shortDateTime(c.createdAt)}</span>
                                    </div>
                                    {c.isGeneral
                                        ? <span className='essayRev__noteChip'>General comment</span>
                                        : c.anchorText && <span className='essayRev__noteAnchor'>"{c.anchorText}"</span>}
                                    {editingId === c.id ? (
                                        <div className='essayRev__noteEdit'>
                                            <textarea
                                                className='essayRev__noteEditInput'
                                                value={editBody}
                                                onChange={e => setEditBody(e.target.value)}
                                                rows={3}
                                                autoFocus
                                            />
                                            <div className='essayRev__noteEditAct'>
                                                <button className='essayRev__btn essayRev__btn--ghost' onClick={cancelEdit}>Cancel</button>
                                                <button className='essayRev__btn essayRev__btn--primary' onClick={saveEdit} disabled={!editBody.trim()}>Save</button>
                                            </div>
                                        </div>
                                    ) : (
                                        <>
                                            <div className='essayRev__noteBody'>{c.body}</div>
                                            <div className='essayRev__noteFoot'>
                                                {c.sourceSuggestionId && <span className='essayRev__fromAI'>from AI</span>}
                                                <span className='essayRev__noteActions'>
                                                    <button onClick={() => startEdit(c)}>Edit</button>
                                                    <button className='essayRev__danger' onClick={() => deleteComment(c)}>Delete</button>
                                                </span>
                                            </div>
                                        </>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* AI panel */}
                {!compareOn && (
                <aside className='essayRev__ai'>
                    {/* draft picker — dropdown replaces the old drafts rail */}
                    <div className='essayRev__draftPick' ref={draftPickRef}>
                        <button type='button' className='essayRev__ddBtn' onClick={() => setDraftsOpen(o => !o)}>
                            <span className='essayRev__ddMain'>
                                <span className='essayRev__ddLabel'>{version.label}</span>
                                <span className='essayRev__ddMeta'>
                                    {version.wordCount} words · {version.submittedAt ? `submitted ${shortDate(version.submittedAt)}` : 'not submitted'}
                                </span>
                            </span>
                            {(() => {
                                const cur = drafts.find(d => d.id === version.id);
                                return cur ? <DraftStatePill d={liveDraft(cur)} /> : null;
                            })()}
                            <MdKeyboardArrowDown size={19} className={`essayRev__ddArrow ${draftsOpen ? 'essayRev__ddArrow--up' : ''}`} />
                        </button>
                        {draftsOpen && (
                            <div className='essayRev__ddList'>
                                {drafts.map(d => (
                                    <button
                                        key={d.id}
                                        type='button'
                                        className={`essayRev__draft ${d.id === version.id ? 'essayRev__draft--on' : ''}`}
                                        onClick={() => { setDraftsOpen(false); if (d.id !== version.id) navigate(`/college-apps/essays/version/${d.id}`); }}
                                    >
                                        <span className='essayRev__draftTop'>
                                            <span className='essayRev__draftName'>{d.label}</span>
                                            <DraftStatePill d={liveDraft(d)} />
                                        </span>
                                        <span className='essayRev__draftMeta'>
                                            {d.wordCount} words · {d.submittedAt ? `submitted ${shortDate(d.submittedAt)}` : 'not submitted'}
                                            {liveDraft(d).commentCount > 0 ? ` · ${liveDraft(d).commentCount} shared` : ''}
                                        </span>
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Supplements are manual-review only — the rubric is written for the
                        650-word personal statement. No review checklist here on purpose:
                        one was tried (swap test, "names not categories", …) and removed
                        because those are "Why us?"-type heuristics that misguide on other
                        supplement types (community, activity-elaboration, short answers). */}
                    {isSupp && (
                        <div className='essayRev__aiCard'>
                            <div className='essayRev__suppGuide'>
                                <p className='essayRev__muted essayRev__suppNote'>
                                    AI review isn't available for supplemental essays — select text in the
                                    essay to leave comments.
                                </p>
                            </div>
                        </div>
                    )}

                    {!isSupp && (
                    <div className='essayRev__aiCard'>
                        <div className='essayRev__aiHead'>
                            <span className='essayRev__aiTitle'><span className='essayRev__spark'><MdAutoAwesome size={15} /></span> AI analysis</span>
                            <span className='essayRev__pill'>Tutor-only</span>
                        </div>

                        {runStatus === 'running' && (
                            <div className='essayRev__score' aria-busy='true' aria-label={`Analyzing ${version.label}…`}>
                                <div className='essayRev__ring'>
                                    <svg viewBox='0 0 100 100'>
                                        <circle className='essayRev__skelTrack' cx='50' cy='50' r='42' />
                                        <circle className='essayRev__skelArc' cx='50' cy='50' r='42' />
                                    </svg>
                                </div>
                                <div className='essayRev__bars'>
                                    {CATEGORY_ORDER.map(key => (
                                        <div key={key} className='essayRev__barRow'>
                                            <span className='essayRev__skel essayRev__skel--label' />
                                            <span className='essayRev__barT'><span className='essayRev__skel essayRev__skel--fill' /></span>
                                            <span />
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {runStatus !== 'running' && analysis?.status === 'succeeded' && (
                            <ScoreCard key={analysis.id} analysis={analysis} />
                        )}

                        {runStatus !== 'running' && analysis?.status !== 'succeeded' && (
                            <div className='essayRev__aiState'>
                                <p className='essayRev__muted'>
                                    Review this draft by selecting text and commenting — or run an optional AI pass for a rubric
                                    score and suggestions. Tutor-only; nothing is shared with the student until you send a comment.
                                </p>
                                {runStatus === 'failed' && <div className='essayRev__aiError'>{runError || 'Analysis failed.'}</div>}
                                {canReview && hasContent ? (
                                    <button className='essayRev__btn essayRev__btn--ai' onClick={runAnalysis}><MdAutoAwesome size={15} /> Run AI analysis</button>
                                ) : (
                                    <p className='essayRev__muted'>
                                        {!isLatest ? 'Open the latest draft to run analysis.'
                                            : !isSubmitted ? 'Available once the student submits this draft.'
                                            : 'This draft is empty — there is nothing to analyze.'}
                                    </p>
                                )}
                            </div>
                        )}
                    </div>
                    )}

                    {/* Suggestions stick while the tutor scrolls the essay;
                        the draft picker and score card above scroll away normally. */}
                    {!isSupp && analysis?.status === 'succeeded' && (
                    <div className='essayRev__aiSticky'>
                        <div className='essayRev__aiCard'>
                            <div className='essayRev__suggHead'>
                                <h4>Suggestions</h4>
                                <span className='essayRev__suggCount'>{pendingSuggestions.length}</span>
                            </div>
                            <p className='essayRev__suggNote'>
                                {isLatest
                                    ? "Click a suggestion to comment on that line. It leaves this list once you've commented — and like all comments, stays a private draft until you send it."
                                    : 'Open the latest draft to act on these suggestions.'}
                            </p>
                            <div className={`essayRev__suggList ${isLatest ? '' : 'essayRev__suggList--ro'}`}>
                                {pendingSuggestions.length === 0 && (
                                    <p className='essayRev__suggEmpty'>All suggestions addressed.</p>
                                )}
                                {pendingSuggestions.map(s => (
                                    <div
                                        key={s.id}
                                        className={`essayRev__sugg ${hoverSuggId === s.id && !s.isGeneral && s.anchorText ? 'essayRev__sugg--hover' : ''}`}
                                        // A general suggestion has no span to anchor a composer to —
                                        // it prefills the whole-essay comment box instead.
                                        onClick={() => (s.isGeneral || !s.anchorText
                                            ? openFromGeneralSuggestion(s)
                                            : openFromSuggestion(s))}
                                        onMouseEnter={() => { if (!s.isGeneral && s.anchorText) setHoverSuggId(s.id); }}
                                        onMouseLeave={() => setHoverSuggId(null)}
                                    >
                                        <div className='essayRev__suggTop'>
                                            <span className='essayRev__suggCat'>{SUGGESTION_CATEGORY_LABELS[s.category] || s.category}</span>
                                            {(s.isGeneral || !s.anchorText) && <span className='essayRev__suggWhole'>Whole essay</span>}
                                            <span className={`essayRev__sev essayRev__sev--${s.severity}`}>{SEVERITY_LABELS[s.severity] || s.severity}</span>
                                        </div>
                                        {s.anchorText && <div className='essayRev__suggAnchor'>"{s.anchorText}"</div>}
                                        <div className='essayRev__suggBody'>{s.body}</div>
                                    </div>
                                ))}
                            </div>
                        </div>

                    </div>
                    )}
                </aside>
                )}
            </div>
        </div>
    );
}

// Score card with per-axis detail on demand: clicking a bar row opens that axis's
// rationale below the card (click again to close, click another to switch). The
// countable evidence (rubric v6+) renders inside the axis it drives — value chips
// under Character, "so what" rows under Reflection. Display-only text: the earlier
// click-to-scroll-into-the-essay affordance confused more than it helped.
// Mount with key={analysis.id} so the open row resets when the analysis changes.
function ScoreCard({ analysis }: { analysis: EssayAnalysis }) {
    // First axis open by default — the rows carry no other "I am clickable" signal,
    // so an already-selected one is what teaches the interaction.
    const [openKey, setOpenKey] = useState<string | null>(CATEGORY_ORDER[0]);
    const rubric = analysis.rubric || {};
    const categories = rubric.categories || {};
    const overall = rubric.overallScore;
    const offset = overall != null ? RING_CIRC * (1 - overall / 5) : RING_CIRC;
    const open = openKey ? categories[openKey] : null;
    const values = rubric.evidence?.valuesNamed || [];
    const insights = rubric.evidence?.insightMoments || [];

    return (
        <>
            <div className='essayRev__score'>
                <div className='essayRev__ring'>
                    <svg viewBox='0 0 100 100'>
                        <circle className='essayRev__ringBg' cx='50' cy='50' r='42' />
                        <circle className='essayRev__ringFg' cx='50' cy='50' r='42' style={{ strokeDashoffset: offset }} />
                    </svg>
                    <div className='essayRev__ringLbl'>
                        <span className='essayRev__ringN'>{overall != null ? overall.toFixed(1) : '—'}</span>
                        <span className='essayRev__ringS'>/ 5</span>
                    </div>
                </div>
                <div className='essayRev__bars'>
                    {CATEGORY_ORDER.map(key => {
                        const score = categories[key]?.score ?? 0;
                        return (
                            <div
                                key={key}
                                className={`essayRev__barRow essayRev__barRow--btn ${openKey === key ? 'essayRev__barRow--open' : ''}`}
                                onClick={() => setOpenKey(k => (k === key ? null : key))}
                            >
                                <span className='essayRev__barL'>{CATEGORY_LABELS[key]}</span>
                                <span className='essayRev__barT'><span className='essayRev__barF' style={{ width: `${(score / 5) * 100}%` }} /></span>
                                <span className='essayRev__barN'>{score}</span>
                            </div>
                        );
                    })}
                </div>
            </div>
            {openKey && open && (
                <div className='essayRev__brkDetail'>
                    <div className='essayRev__brkHead'>
                        <span className='essayRev__brkName'>{CATEGORY_LABELS[openKey]}</span>
                        <span className='essayRev__brkScore'>{open.score}</span>
                    </div>
                    {open.rationale
                        ? <p className='essayRev__brkText'>{open.rationale}</p>
                        : <p className='essayRev__brkText'>No rationale was recorded for this category.</p>}
                    {openKey === 'character' && values.length > 0 && (
                        <div className='essayRev__evChips'>
                            {values.map((v, i) => (
                                <span key={i} className={`essayRev__evChip ${v.shown ? '' : 'essayRev__evChip--asserted'}`}>
                                    {v.value}
                                    {!v.shown && <span className='essayRev__evChipNote'>claimed</span>}
                                </span>
                            ))}
                        </div>
                    )}
                    {openKey === 'reflection' && insights.length > 0 && (
                        <div className='essayRev__evList'>
                            {insights.map((m, i) => (
                                <div key={i} className='essayRev__evInsight'>
                                    <span className={`essayRev__evQ ${m.quality === 'illuminating' ? 'essayRev__evQ--illum' : ''}`}>
                                        {m.quality === 'illuminating' ? 'illuminating' : 'predictable'}
                                    </span>
                                    <span className='essayRev__evSummary'>{m.summary}</span>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}
        </>
    );
}

function initials(name: string | null): string {
    if (!name) return '';
    return name.split(/\s+/).map(p => p[0]).join('').slice(0, 2).toUpperCase();
}

function shortDate(iso: string): string {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

function shortDateTime(iso: string): string {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

export default FillAppEssayReview;
